/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useState, useEffect, useCallback, useMemo, type JSX } from "react";
import { type MatrixClient, type Room as MatrixRoom } from "matrix-js-sdk";
import { InputField } from "../input/Input";
import { Avatar } from "../Avatar";
import CloseIcon from "../icons/Close.svg?react";
import styles from "./PollPane.module.css";

interface Poll {
  poll_id: string;
  question: string;
  options: string[];
  anonymous: boolean;
  creator: string;
  timestamp: number;
  multi_select: boolean;
  allow_tagging: boolean;
  allow_other: boolean;
}

interface CreatorOption {
  value: string;
  isOther: boolean;
}

interface PollPaneProps {
  client: MatrixClient;
  room: MatrixRoom;
  onClose: () => void;
}

interface OtherOptionFieldProps {
  idx: number;
  isClosed: boolean;
  onVote: (text: string) => void;
}

const OtherOptionField: FC<OtherOptionFieldProps> = ({
  isClosed,
  onVote,
}) => {
  const [text, setText] = useState("");

  return (
    <div className={styles.optionWrapper} style={{ marginTop: 8 }}>
      {!isClosed && (
        <div className={styles.otherInputFieldRow}>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter response"
            className={styles.otherInput}
          />
          <button
            onClick={() => {
              onVote(text);
              setText("");
            }}
            disabled={!text.trim()}
            className={styles.otherVoteBtn}
          >
            Submit
          </button>
        </div>
      )}
    </div>
  );
};

interface TagSectionProps {
  allowTagging: boolean;
  isAnonymous: boolean;
  room: MatrixRoom;
  optTags: Record<string, string[]>; // taggedUserId -> senders[]
  myUserId: string;
  onTag: (taggedUserId: string) => void;
}

const TagSection: FC<TagSectionProps> = ({
  allowTagging,
  isAnonymous,
  room,
  optTags,
  myUserId,
  onTag,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  if (!allowTagging) return null;
  
  const members = room.getMembers();
  const hasMyTag = Object.values(optTags).some((senders) => senders.includes(myUserId));
  
  return (
    <div className={styles.taggingSection}>
      <div className={styles.tagRow}>
        {/* Render existing tags as selectable buttons */}
        {Object.entries(optTags).map(([taggedId, senders]) => {
          const m = room.getMember(taggedId);
          const name = m?.name || taggedId;
          const avatarUrl = m?.getMxcAvatarUrl() || undefined;
          
          const count = senders.length;
          const isMyTagged = senders.includes(myUserId);
          const authors = senders.map((s) => room.getMember(s)?.name || s).join(", ");
          
          return (
            <button
              key={taggedId}
              onClick={() => onTag(taggedId)}
              className={`${styles.tagBadgeButton} ${isMyTagged ? styles.tagBadgeActive : ""}`}
              title={isAnonymous ? undefined : `Tagged by: ${authors}`}
            >
              <Avatar id={taggedId} name={name} src={avatarUrl} size={14} />
              <span>{name}</span>
              {count > 1 && <span className={styles.tagCount}>({count})</span>}
            </button>
          );
        })}
        
        {/* Tag button */}
        {!hasMyTag && (
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={styles.tagBtn}
            title="Tag participant"
          >
            <span>🏷️ Tag</span>
          </button>
        )}
      </div>
      
      {dropdownOpen && (
        <div className={styles.tagDropdown}>
          {members.map((member) => {
            const name = member.name;
            const avatarUrl = member.getMxcAvatarUrl() || undefined;
            return (
              <div
                key={member.userId}
                onClick={() => {
                  onTag(member.userId);
                  setDropdownOpen(false);
                }}
                className={styles.dropdownMemberRow}
              >
                <Avatar id={member.userId} name={name} src={avatarUrl} size={18} />
                <span>{name}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const PollPane: FC<PollPaneProps> = ({ client, room, onClose }) => {
  const [activeTab, setActiveTab] = useState<"current" | "create" | "history">("current");
  
  // State for all parsed polls, votes and closed status with timestamps for real-time reopening
  const [polls, setPolls] = useState<Record<string, Poll>>({});
  const [votes, setVotes] = useState<Record<string, Record<string, number[]>>>({});
  const [closedPolls, setClosedPolls] = useState<Record<string, number>>({});

  // Dynamic user write-in custom values for other fields: poll_id -> sender -> text
  const [writeIns, setWriteIns] = useState<Record<string, Record<string, string>>>({}); // poll_id -> sender -> text

  // Dynamically parsed tags: poll_id -> option_index -> tagged_user_id -> sender_user_ids[]
  const [tags, setTags] = useState<Record<string, Record<number, Record<string, string[]>>>>({});

  // Expanded status for historical polls (collapsed by default)
  const [expandedPolls, setExpandedPolls] = useState<Set<string>>(new Set());

  // Creator Form States
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<CreatorOption[]>([]); // Seeded with initial 0 options
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [allowTagging, setAllowTagging] = useState(false);
  const [createError, setCreateError] = useState("");

  const myUserId = useMemo(() => client.getUserId() || "", [client]);

  // Parse historical timeline events
  useEffect(() => {
    const timelineEvents = room.getLiveTimeline().getEvents();
    const loadedPolls: Record<string, Poll> = {};
    const loadedVotes: Record<string, Record<string, number[]>> = {};
    const loadedClosed: Record<string, number> = {};
    const loadedWriteIns: Record<string, Record<string, string>> = {};
    const loadedTags: Record<string, Record<number, Record<string, string[]>>> = {};
    const loadedDeleted = new Set<string>();

    // Pass 1: Parse all polls, close and delete events to get the latest start and end timestamps
    timelineEvents.forEach((event) => {
      const type = event.getType();
      const content = event.getContent();
      const sender = event.getSender();

      if (!content || !sender) return;

      if (type === "org.matrix.custom.poll.start") {
        const currentTs = loadedPolls[content.poll_id]?.timestamp || 0;
        if (event.getTs() >= currentTs) {
          loadedPolls[content.poll_id] = {
            poll_id: content.poll_id,
            question: content.question,
            options: Array.isArray(content.options) ? [...content.options] : [],
            anonymous: !!content.anonymous,
            creator: sender,
            timestamp: event.getTs(),
            multi_select: !!content.multi_select,
            allow_tagging: !!content.allow_tagging,
            allow_other: !!content.allow_other,
          };
        }
      } else if (type === "org.matrix.custom.poll.end") {
        const currentTs = loadedClosed[content.poll_id] || 0;
        if (event.getTs() >= currentTs) {
          loadedClosed[content.poll_id] = event.getTs();
        }
      } else if (type === "org.matrix.custom.poll.delete") {
        loadedDeleted.add(content.poll_id);
      }
    });

    // Pass 2: Aggregate write-ins, votes, and tags that occurred *after* or *at* the latest start event
    timelineEvents.forEach((event) => {
      const type = event.getType();
      const content = event.getContent();
      const sender = event.getSender();

      if (!content || !sender) return;

      const pollId = content.poll_id;
      const poll = loadedPolls[pollId];
      if (!poll) return;

      // Crucial: Only accept events that happened during the current active run of this poll!
      if (event.getTs() < poll.timestamp) return;

      if (type === "org.matrix.custom.poll.write_in") {
        if (!loadedWriteIns[pollId]) {
          loadedWriteIns[pollId] = {};
        }
        loadedWriteIns[pollId][sender] = content.option;
      } else if (type === "org.matrix.custom.poll.vote") {
        if (!loadedVotes[pollId]) {
          loadedVotes[pollId] = {};
        }
        if (Array.isArray(content.option_indices)) {
          loadedVotes[pollId][sender] = content.option_indices.map(Number);
        } else if (content.option_index !== undefined) {
          loadedVotes[pollId][sender] = [Number(content.option_index)];
        }
      } else if (type === "org.matrix.custom.poll.tag") {
        const optIdx = Number(content.option_index);
        const taggedId = content.tagged_user_id;

        if (!loadedTags[pollId]) {
          loadedTags[pollId] = {};
        }
        if (!loadedTags[pollId][optIdx]) {
          loadedTags[pollId][optIdx] = {};
        }

        const optTags = loadedTags[pollId][optIdx];

        // Remove sender's previous tags for this option
        Object.keys(optTags).forEach((tId) => {
          optTags[tId] = optTags[tId].filter((s) => s !== sender);
          if (optTags[tId].length === 0) {
            delete optTags[tId];
          }
        });

        // Add sender to the new tagged user
        if (taggedId) {
          if (!optTags[taggedId]) {
            optTags[taggedId] = [];
          }
          if (!optTags[taggedId].includes(sender)) {
            optTags[taggedId].push(sender);
          }
        }
      }
    });

    // Apply loaded write-ins to corresponding polls options lists
    Object.entries(loadedWriteIns).forEach(([pollId, userWriteIns]) => {
      const poll = loadedPolls[pollId];
      if (poll) {
        Object.values(userWriteIns).forEach((customOpt) => {
          if (!poll.options.includes(customOpt)) {
            poll.options.push(customOpt);
          }
        });
      }
    });

    // Filter out deleted polls permanently
    loadedDeleted.forEach((pollId) => {
      delete loadedPolls[pollId];
    });

    setPolls(loadedPolls);
    setVotes(loadedVotes);
    setClosedPolls(loadedClosed);
    setWriteIns(loadedWriteIns);
    setTags(loadedTags);

    // Default tab navigation
    const hasActivePoll = Object.keys(loadedPolls).some((id) => {
      const endTs = loadedClosed[id] || 0;
      return endTs === 0 || endTs < (loadedPolls[id]?.timestamp || 0);
    });
    setActiveTab(hasActivePoll ? "current" : "create");
  }, [room, client]);

  // Real-time timeline listener
  useEffect(() => {
    const handleRoomEvent = (event: any) => {
      if (event.getRoomId() !== room.roomId) return;

      const type = event.getType();
      const content = event.getContent();
      const sender = event.getSender();

      if (!content || !sender) return;

      if (type === "org.matrix.custom.poll.start") {
        setPolls((prev) => {
          const currentTs = prev[content.poll_id]?.timestamp || 0;
          if (event.getTs() >= currentTs) {
            // Reopened/Reactivated! Reset all local votes, write-ins, and tags of this poll
            setVotes((prevVotes) => {
              const next = { ...prevVotes };
              delete next[content.poll_id];
              return next;
            });
            setWriteIns((prevWriteIns) => {
              const next = { ...prevWriteIns };
              delete next[content.poll_id];
              return next;
            });
            setTags((prevTags) => {
              const next = { ...prevTags };
              delete next[content.poll_id];
              return next;
            });

            return {
              ...prev,
              [content.poll_id]: {
                poll_id: content.poll_id,
                question: content.question,
                options: Array.isArray(content.options) ? [...content.options] : [],
                anonymous: !!content.anonymous,
                creator: sender,
                timestamp: event.getTs(),
                multi_select: !!content.multi_select,
                allow_tagging: !!content.allow_tagging,
                allow_other: !!content.allow_other,
              },
            };
          }
          return prev;
        });
        setActiveTab("current");
      } else if (type === "org.matrix.custom.poll.delete") {
        setPolls((prev) => {
          const next = { ...prev };
          delete next[content.poll_id];
          return next;
        });
      } else {
        // Crucial: Only accept other events if they occurred after the latest start event timestamp!
        const pollId = content.poll_id;
        const poll = polls[pollId];
        if (!poll || event.getTs() < poll.timestamp) return;

        if (type === "org.matrix.custom.poll.write_in") {
          setWriteIns((prev) => {
            const pollWriteIns = { ...(prev[pollId] || {}) };
            pollWriteIns[sender] = content.option;
            return {
              ...prev,
              [pollId]: pollWriteIns,
            };
          });
          setPolls((prev) => {
            const currentPoll = prev[pollId];
            if (currentPoll && !currentPoll.options.includes(content.option)) {
              const nextOptions = [...currentPoll.options, content.option];
              return {
                ...prev,
                [pollId]: {
                  ...currentPoll,
                  options: nextOptions,
                },
              };
            }
            return prev;
          });
        } else if (type === "org.matrix.custom.poll.vote") {
          setVotes((prev) => {
            const pollVotes = { ...(prev[pollId] || {}) };
            if (Array.isArray(content.option_indices)) {
              pollVotes[sender] = content.option_indices.map(Number);
            } else if (content.option_index !== undefined) {
              pollVotes[sender] = [Number(content.option_index)];
            }
            return {
              ...prev,
              [pollId]: pollVotes,
            };
          });
        } else if (type === "org.matrix.custom.poll.tag") {
          const optIdx = Number(content.option_index);
          const taggedId = content.tagged_user_id;

          setTags((prev) => {
            const pollTags = { ...(prev[pollId] || {}) };
            const optTags = { ...(pollTags[optIdx] || {}) };

            // Remove sender's previous tags for this option
            Object.keys(optTags).forEach((tId) => {
              optTags[tId] = optTags[tId].filter((s) => s !== sender);
              if (optTags[tId].length === 0) {
                delete optTags[tId];
              }
            });

            // Add sender to the new tagged user
            if (taggedId) {
              if (!optTags[taggedId]) {
                optTags[taggedId] = [];
              }
              if (!optTags[taggedId].includes(sender)) {
                optTags[taggedId].push(sender);
              }
            }

            pollTags[optIdx] = optTags;
            return {
              ...prev,
              [pollId]: pollTags,
            };
          });
        } else if (type === "org.matrix.custom.poll.end") {
          setClosedPolls((prev) => {
            const currentTs = prev[pollId] || 0;
            if (event.getTs() >= currentTs) {
              return {
                ...prev,
                [pollId]: event.getTs(),
              };
            }
            return prev;
          });
        }
      }
    };

    (client as any).on("Room.timeline", handleRoomEvent);
    return () => {
      (client as any).off("Room.timeline", handleRoomEvent);
    };
  }, [client, room.roomId, polls]);

  // Find latest active poll
  const activePoll = useMemo(() => {
    const activeList = Object.values(polls)
      .filter((p) => {
        const endTs = closedPolls[p.poll_id] || 0;
        return endTs === 0 || endTs < p.timestamp;
      })
      .sort((a, b) => b.timestamp - a.timestamp);
    return activeList[0] || null;
  }, [polls, closedPolls]);

  // Find closed polls
  const previousPolls = useMemo(() => {
    return Object.values(polls)
      .filter((p) => {
        const endTs = closedPolls[p.poll_id] || 0;
        return endTs > 0 && endTs >= p.timestamp;
      })
      .sort((a, b) => b.timestamp - a.timestamp);
  }, [polls, closedPolls]);

  // Create/Launch survey poll
  const handleLaunchPoll = useCallback(async () => {
    setCreateError("");
    const trimmedQuestion = question.trim();

    // Only map predefined non-empty standard options
    const validPredefinedOptions = options
      .filter((opt) => !opt.isOther && opt.value.trim() !== "")
      .map((opt) => opt.value.trim());

    if (!trimmedQuestion) {
      setCreateError("Please enter a question.");
      return;
    }

    const pollId = `poll-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const hasOther = options.some((opt) => opt.isOther);

    try {
      await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.start", {
        poll_id: pollId,
        question: trimmedQuestion,
        options: validPredefinedOptions,
        anonymous: isAnonymous,
        multi_select: isMultiSelect,
        allow_tagging: allowTagging,
        allow_other: hasOther,
      });

      // Clear Form State
      setQuestion("");
      setOptions([]);
      setIsAnonymous(false);
      setIsMultiSelect(false);
      setAllowTagging(false);
      setActiveTab("current");
    } catch (err: any) {
      setCreateError(err.message || "Failed to launch poll.");
    }
  }, [client, room.roomId, question, options, isAnonymous, isMultiSelect, allowTagging]);

  // Vote on standard options
  const handleVote = useCallback(async (pollId: string, optionIndex: number) => {
    const poll = polls[pollId];
    if (!poll) return;

    try {
      if (poll.multi_select) {
        const currentVotes = votes[pollId]?.[myUserId] || [];
        let nextVotes: number[];
        if (currentVotes.includes(optionIndex)) {
          nextVotes = currentVotes.filter((idx) => idx !== optionIndex);
        } else {
          nextVotes = [...currentVotes, optionIndex];
        }
        await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.vote", {
          poll_id: pollId,
          option_indices: nextVotes,
        });
      } else {
        await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.vote", {
          poll_id: pollId,
          option_index: optionIndex,
        });
      }
    } catch (err) {
      console.error("Failed to submit vote", err);
    }
  }, [client, room.roomId, polls, votes, myUserId]);

  // Respondent adds a Write-in ("Other") secret option
  const handleWriteInSubmit = useCallback(async (pollId: string, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    // Check if respondent already added an other option
    const hasWriteIn = !!writeIns[pollId]?.[myUserId];
    if (hasWriteIn) return;

    try {
      // 1. Send the write-in custom event
      await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.write_in", {
        poll_id: pollId,
        option: trimmed,
      });

      // 2. Automatically vote for this write-in option (which gets appended to the end of the options list)
      const currentPollOptions = polls[pollId]?.options || [];
      const newIndex = currentPollOptions.length;

      if (polls[pollId]?.multi_select) {
        const currentVotes = votes[pollId]?.[myUserId] || [];
        const nextVotes = [...currentVotes, newIndex];
        await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.vote", {
          poll_id: pollId,
          option_indices: nextVotes,
        });
      } else {
        await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.vote", {
          poll_id: pollId,
          option_index: newIndex,
        });
      }
    } catch (err) {
      console.error("Failed to submit other option", err);
    }
  }, [client, room.roomId, polls, votes, writeIns, myUserId]);

  // Tag a participant for a specific option position
  const handleTag = useCallback(async (pollId: string, optionIndex: number, taggedUserId: string) => {
    const optionTags = tags[pollId]?.[optionIndex] || {};
    
    // Toggle behavior: untag if selecting the same user they previously tagged
    const previouslyTagged = Object.entries(optionTags).find(([_, senders]) => senders.includes(myUserId));
    const targetUserId = previouslyTagged && previouslyTagged[0] === taggedUserId ? "" : taggedUserId;

    try {
      await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.tag", {
        poll_id: pollId,
        option_index: optionIndex,
        tagged_user_id: targetUserId,
      });
    } catch (err) {
      console.error("Failed to submit tag", err);
    }
  }, [client, room.roomId, tags, myUserId]);

  // Close Poll
  const handleClosePoll = useCallback(async (pollId: string) => {
    try {
      await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.end", {
        poll_id: pollId,
      });
    } catch (err) {
      console.error("Failed to close poll", err);
    }
  }, [client, room.roomId]);

  // Delete Poll
  const handleDeletePoll = useCallback(async (pollId: string) => {
    try {
      await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.delete", {
        poll_id: pollId,
      });
    } catch (err) {
      console.error("Failed to delete poll", err);
    }
  }, [client, room.roomId]);

  // Reopen Poll
  const handleReopenPoll = useCallback(async (pollId: string) => {
    const poll = polls[pollId];
    if (!poll) return;

    try {
      await (client as any).sendEvent(room.roomId, "org.matrix.custom.poll.start", {
        poll_id: poll.poll_id,
        question: poll.question,
        options: poll.options.filter((opt) => {
          // Keep only original predefined options for clean reopening
          const pollWriteIns = Object.values(writeIns[poll.poll_id] || {});
          return !pollWriteIns.includes(opt);
        }),
        anonymous: poll.anonymous,
        multi_select: poll.multi_select,
        allow_tagging: poll.allow_tagging,
        allow_other: poll.allow_other,
      });
      setActiveTab("current");
    } catch (err) {
      console.error("Failed to reopen poll", err);
    }
  }, [client, room.roomId, polls, writeIns]);

  // Expand/collapse toggle for history tab
  const toggleExpand = useCallback((pollId: string) => {
    setExpandedPolls((prev) => {
      const next = new Set(prev);
      if (next.has(pollId)) {
        next.delete(pollId);
      } else {
        next.add(pollId);
      }
      return next;
    });
  }, []);

  // Option actions for creation
  const handleAddOption = useCallback(() => {
    if (options.length >= 10) return;
    setOptions((prev) => [...prev, { value: "", isOther: false }]);
  }, [options]);

  const handleAddOther = useCallback(() => {
    if (options.length >= 10) return;
    setOptions((prev) => [...prev, { value: "", isOther: true }]);
  }, [options]);

  const handleRemoveOption = useCallback((index: number) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleOptionChange = useCallback((index: number, val: string) => {
    setOptions((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], value: val };
      return next;
    });
  }, []);

  // Render a poll card (Voter / Results / Expanding states)
  const renderPollCard = (poll: Poll, isClosed: boolean): JSX.Element => {
    const pollVotes = votes[poll.poll_id] || {};
    const totalVoters = Object.keys(pollVotes).length;
    const myVotesList = pollVotes[myUserId] || [];
    const isExpanded = !isClosed || expandedPolls.has(poll.poll_id);
    const totalVotes = Object.values(pollVotes).reduce((sum, v) => sum + v.length, 0);

    const hasMyWriteIn = !!writeIns[poll.poll_id]?.[myUserId];

    return (
      <div
        key={poll.poll_id}
        className={`${styles.pollCard} ${isClosed ? styles.historicalCard : ""}`}
        onClick={isClosed ? () => toggleExpand(poll.poll_id) : undefined}
      >
        <div className={styles.pollCardHeader}>
          <div className={styles.headerTitleRow}>
            <h3 className={styles.pollQuestion}>
              {poll.question}
            </h3>
            {isClosed && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpand(poll.poll_id);
                }}
                className={styles.caretBtn}
                aria-label={isExpanded ? "Collapse poll" : "Expand poll"}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{
                    transform: isExpanded ? "rotate(90deg)" : "none",
                    transition: "transform 0.15s ease",
                  }}
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            )}
          </div>
          <span className={styles.pollMeta}>
            {totalVotes} {totalVotes === 1 ? "vote" : "votes"}
            {isClosed && (
              <div className={styles.closedMetaContainer}>
                • <span className={styles.closedBadge}>Closed</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleReopenPoll(poll.poll_id);
                  }}
                  className={styles.reopenBtn}
                  title="Reopen poll and make active"
                  aria-label="Reopen poll and make active"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M1 4v6h6" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                  </svg>
                </button>
              </div>
            )}
          </span>
        </div>

        {isExpanded && (
          <>
            <div className={styles.optionsList}>
              {poll.options.map((opt, idx) => {
                const optVotes = Object.entries(pollVotes).filter(([_, valList]) => valList.includes(idx));
                const optCount = optVotes.length;
                const percentage = totalVoters > 0 ? Math.round((optCount / totalVoters) * 100) : 0;
                const isMySelected = myVotesList.includes(idx);

                // Get tags for this option index
                const optTags = tags[poll.poll_id]?.[idx] || {};

                const isOtherResponse = Object.values(writeIns[poll.poll_id] || {}).includes(opt);

                return (
                  <div key={idx} className={styles.optionWrapper} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {isOtherResponse ? (
                      <div className={styles.optionTextCard}>
                        <span className={styles.optionText}>{opt}</span>
                      </div>
                    ) : (
                      <button
                        disabled={isClosed}
                        onClick={() => handleVote(poll.poll_id, idx)}
                        className={`${styles.optionButton} ${isMySelected ? styles.optionSelected : ""}`}
                      >
                        <div className={styles.optionContent}>
                          <span className={styles.optionText}>{opt}</span>
                          <span className={styles.optionPercent}>{percentage}% ({optCount})</span>
                        </div>
                        <div
                          className={styles.progressBar}
                          style={{ width: `${percentage}%` }}
                        />
                      </button>
                    )}

                    {/* Show voter avatars for non-anonymous options */}
                    {!isOtherResponse && !poll.anonymous && optCount > 0 && (
                      <div className={styles.votersList}>
                        {optVotes.map(([voterId]) => {
                          const m = room.getMember(voterId);
                          const name = m?.name || voterId;
                          const avatarUrl = m?.getMxcAvatarUrl() || undefined;
                          return (
                            <div key={voterId} className={styles.voterAvatarWrapper} title={name}>
                              <Avatar id={voterId} name={name} src={avatarUrl} size={18} />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Render Tagging section if enabled */}
                    {poll.allow_tagging && (
                      <TagSection
                        allowTagging={poll.allow_tagging}
                        isAnonymous={poll.anonymous}
                        room={room}
                        optTags={optTags}
                        myUserId={myUserId}
                        onTag={(tagId) => handleTag(poll.poll_id, idx, tagId)}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Respondent "+ Add Other" (secret write-in) text field */}
            {!isClosed && poll.allow_other && !hasMyWriteIn && (
              <OtherOptionField
                idx={poll.options.length}
                isClosed={isClosed}
                onVote={(text) => handleWriteInSubmit(poll.poll_id, text)}
              />
            )}

            {!isClosed && (
              <button
                onClick={() => handleClosePoll(poll.poll_id)}
                className={styles.closePollButton}
                style={{ marginTop: 12 }}
              >
                End poll
              </button>
            )}

            {isClosed && (
              <div className={styles.deleteRow}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePoll(poll.poll_id);
                  }}
                  className={styles.deletePollButton}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const activePollsCount = useMemo(() => {
    return Object.values(polls).filter((p) => {
      const endTs = closedPolls[p.poll_id] || 0;
      return endTs === 0 || endTs < p.timestamp;
    }).length;
  }, [polls, closedPolls]);

  return (
    <div className={styles.pollPane} data-testid="poll-pane">
      <div className={styles.header}>
        <h2 className={styles.title}>Create a poll</h2>
        <button onClick={onClose} className={styles.closeBtn} aria-label="Close pane">
          <CloseIcon width={16} height={16} />
        </button>
      </div>

      <div className={styles.tabs}>
        <button
          onClick={() => setActiveTab("current")}
          className={`${styles.tabBtn} ${activeTab === "current" ? styles.tabActive : ""}`}
        >
          Active{activePollsCount > 0 ? ` (${activePollsCount})` : ""}
        </button>
        <button
          onClick={() => setActiveTab("create")}
          className={`${styles.tabBtn} ${activeTab === "create" ? styles.tabActive : ""}`}
        >
          Edit
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`${styles.tabBtn} ${activeTab === "history" ? styles.tabActive : ""}`}
        >
          History
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === "current" && (
          activePoll ? renderPollCard(activePoll, false) : (
            <div className={styles.emptyState}>
              <p>No active poll at the moment.</p>
              <button onClick={() => setActiveTab("create")} className={styles.actionBtn}>
                Let's go
              </button>
            </div>
          )
        )}

        {activeTab === "create" && (
          <div className={styles.creatorForm}>
            <div className={styles.creatorScrollBody}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Question</label>
                <input
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Enter question"
                  className={styles.textInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Options (Max 10)</label>
                <div className={styles.optionsCreatorList}>
                  {options.map((opt, idx) => {
                    const standardCount = options.slice(0, idx).filter((o) => !o.isOther).length + 1;
                    const placeholderText = opt.isOther ? "Other Field" : `Enter option ${standardCount}`;

                    return (
                      <div key={idx} className={styles.optionInputRow}>
                        <input
                          type="text"
                          readOnly={opt.isOther}
                          value={opt.value}
                          onChange={(e) => handleOptionChange(idx, e.target.value)}
                          placeholder={placeholderText}
                          className={styles.textInput}
                          style={opt.isOther ? { cursor: "not-allowed" } : undefined}
                        />
                        <button
                          onClick={() => handleRemoveOption(idx)}
                          className={styles.removeOptionBtn}
                          title="Remove option"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            style={{ display: "block" }}
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
                
                <div className={styles.optionsButtonsRow}>
                  {options.length < 10 && (
                    <button onClick={handleAddOption} className={styles.addOptionBtn}>
                      + Add Option
                    </button>
                  )}
                  {options.length < 10 && (
                    <button
                      onClick={handleAddOther}
                      disabled={options.some((opt) => opt.isOther)}
                      className={styles.addOptionBtn}
                      style={options.some((opt) => opt.isOther) ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    >
                      + Add Other
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.checkboxGroup}>
                <InputField
                  id="anonymous-poll"
                  type="checkbox"
                  label="Anonymous poll"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
              </div>

              <div className={styles.checkboxGroup} style={{ marginTop: 4 }}>
                <InputField
                  id="allow-tagging"
                  type="checkbox"
                  label="Allow tagging"
                  checked={allowTagging}
                  onChange={(e) => setAllowTagging(e.target.checked)}
                />
              </div>

              <div className={styles.checkboxGroup} style={{ marginTop: 4 }}>
                <InputField
                  id="multi-select"
                  type="checkbox"
                  label="Select multiple"
                  checked={isMultiSelect}
                  onChange={(e) => setIsMultiSelect(e.target.checked)}
                />
              </div>
            </div>

            <div className={styles.creatorFooter}>
              {createError && <p className={styles.errorText}>{createError}</p>}
              <button onClick={handleLaunchPoll} className={styles.launchBtn}>
                Create
              </button>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          previousPolls.length > 0 ? (
            <div className={styles.historyList}>
              {previousPolls.map((poll) => renderPollCard(poll, true))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No past polls found.</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};
